import { useState } from 'react';
import { callUIAPI } from '../../../utils/apiUtils';
import * as apiConstants from '../../../utils/apiUtilsConstants';
import ConfirmDialog from './ConfirmDialog';

/**
 * Props:
 *   firm          – the firm object
 *   onFirmUpdate  – callback receiving the updated firm object
 *   onCustomerChanged – optional callback fired after payment customer is created/changed
 *                       so parent can signal other sections to refresh
 */
const FirmPaymentId = ({ firm, onFirmUpdate, onCustomerChanged }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [showDialog, setShowDialog] = useState(false);
  const [action, setAction] = useState(null); // 'save' | 'clear'

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const startEditing = () => {
    setValue(firm.paymentCustomerId || '');
    setError(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setError(null);
  };

  /* ── Commit payment ID (shared by save + clear) ─────────────────── */

  const commitPaymentId = async (newValue) => {
    try {
      setSaving(true);
      setError(null);
      const response = await callUIAPI(
        `bo/firms/${firm.id}/payment-customer-id`,
        { paymentCustomerId: newValue },
        apiConstants.METHOD_PUT,
        true,
      );
      onFirmUpdate(response.data.item);
      setEditing(false);
      setShowDialog(false);
      setAction(null);
      onCustomerChanged?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update Payment ID.');
      setShowDialog(false);
      setAction(null);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    setAction('save');
    setError(null);
    setShowDialog(true);
  };

  const handleClearClick = () => {
    setAction('clear');
    setError(null);
    setShowDialog(true);
  };

  const confirmAction = () => {
    if (action === 'clear') commitPaymentId('');
    else commitPaymentId(value);
  };

  const closeDialog = () => {
    if (saving) return;
    setShowDialog(false);
    setAction(null);
  };

  const openCreateDialog = () => {
    setCreateError(null);
    setShowCreateDialog(true);
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setShowCreateDialog(false);
    setCreateError(null);
  };

  const handleCreateCustomer = async () => {
    try {
      setCreating(true);
      setCreateError(null);
      const response = await callUIAPI(
        `bo/firms/${firm.id}/payment-customer`,
        null,
        apiConstants.METHOD_POST,
        true,
      );
      onFirmUpdate(response.data.item);
      setShowCreateDialog(false);
      onCustomerChanged?.();
    } catch (err) {
      setCreateError(err?.response?.data?.message || 'Failed to create Stripe customer.');
    } finally {
      setCreating(false);
    }
  };

  const pendingNewValue = action === 'clear' ? '' : value;
  const isClear = action === 'clear';

  return (
    <div className="firm-field-section">
      <div className="firm-field-row">
        <div className="firm-field-content">
          <h3 className="firm-field-label">Payment ID</h3>
          {!editing ? (
            <p className="firm-field-value">
              {firm.paymentCustomerId || <span className="firm-empty-value">Not set</span>}
            </p>
          ) : (
            <div className="firm-edit-row">
              <input
                className="firm-field-input"
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g. ctm_abc123"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && value.trim()) handleSaveClick();
                  if (e.key === 'Escape') cancelEditing();
                }}
              />
              <button className="firm-btn firm-btn-save" onClick={handleSaveClick} disabled={!value.trim()}>Save</button>
              <button className="firm-btn firm-btn-secondary" onClick={cancelEditing}>Cancel</button>
            </div>
          )}
          {error && <p className="firm-field-error">{error}</p>}
        </div>
        {!editing && (
          <div className="firm-field-actions">
            <button className="firm-btn firm-btn-secondary" onClick={startEditing}>Edit</button>
            {firm.paymentCustomerId && (
              <button className="firm-btn firm-btn-clear" onClick={handleClearClick}>Clear</button>
            )}
            {!firm.paymentCustomerId && (
              <button className="firm-btn firm-btn-create" onClick={openCreateDialog}>Create</button>
            )}
          </div>
        )}
      </div>

      {}
      <ConfirmDialog
        open={showDialog}
        onClose={closeDialog}
        onConfirm={confirmAction}
        title={isClear ? 'Clear Payment ID?' : 'Update Payment ID?'}
        icon={isClear ? '⚠' : 'ℹ️'}
        iconVariant={isClear ? 'warning' : 'info'}
        confirmLabel={isClear ? 'Clear Payment ID' : 'Update Payment ID'}
        confirmVariant={isClear ? 'clear' : 'save'}
        loading={saving}
        loadingText={isClear ? 'Clearing Payment ID…' : 'Updating Payment ID…'}
      >
        <p className="firm-dialog-body">
          {isClear
            ? 'This will remove the Payment ID from this firm. The firm will no longer be linked to a payment provider customer record.'
            : 'Please confirm you want to update the Payment ID for this firm. This may affect subscription and billing lookups.'}
        </p>
        <p className="firm-dialog-field-current">
          Current:{' '}
          {firm.paymentCustomerId
            ? <span className="firm-dialog-field-current-value">{firm.paymentCustomerId}</span>
            : <span className="firm-empty-value">Not set</span>}
        </p>
        <p className="firm-dialog-field-new">
          New:{' '}
          {pendingNewValue
            ? <span className="firm-dialog-field-new-value">{pendingNewValue}</span>
            : <span className="firm-dialog-field-new-value-empty">Empty (will be cleared)</span>}
        </p>
      </ConfirmDialog>

      {}
      <ConfirmDialog
        open={showCreateDialog}
        onClose={closeCreateDialog}
        onConfirm={handleCreateCustomer}
        title="Create Stripe Customer?"
        icon="ℹ️"
        iconVariant="info"
        confirmLabel="Create Stripe Customer"
        confirmVariant="save"
        loading={creating}
        loadingText="Creating Stripe customer…"
        error={createError}
      >
        <p className="firm-dialog-body">
          This will create a new Stripe customer record for <strong>{firm.name}</strong> using
          the primary user&rsquo;s email address and enroll them on the free tier with a trial
          period, matching the standard signup flow.
        </p>
      </ConfirmDialog>
    </div>
  );
};

export default FirmPaymentId;